import { createTheme } from '@mantine/core';

// ---------------------------------------------------------------------------
// MUI -> Mantine migration (Lane A: @mantine/emotion).
//
// During the migration we replace the legacy `withTheme` HOC (which injected
// MUI's theme into class components) with these plain constants. They mirror
// the custom palette tokens defined in the old MUI theme in src/index.js:
//
//   primary.main        -> BRAND_PRIMARY
//   secondary.main      -> BRAND_SECONDARY
//   halfToneColour.main -> HALF_TONE
//   mutedColour.main    -> MUTED
//   divider             -> DIVIDER
//
// Reach for these instead of re-plumbing a theme object into class components.
// ---------------------------------------------------------------------------

export const BRAND_PRIMARY = '#ad1179';
export const BRAND_SECONDARY = '#b8a3bf';
export const HALF_TONE = '#999999';
export const MUTED = '#cccccc';
export const DIVIDER = 'rgba(0, 0, 0, 0.12)';

// Mantine requires colors as a 10-shade tuple. This ramp is anchored so that
// index 6 === BRAND_PRIMARY (#ad1179), which is Mantine's default primaryShade.
const brand = [
  '#fae9f3',
  '#f3cce2',
  '#e9a6cb',
  '#de7fb4',
  '#d65fa1',
  '#d04a95',
  '#ad1179', // 6 - primary brand color
  '#97106a',
  '#800d5a',
  '#6a0b4b',
];

export const mantineTheme = createTheme({
  primaryColor: 'brand',
  primaryShade: 6,
  colors: { brand },
  fontFamily: 'CeraPro, "Noto Sans TC", Roboto, "Helvetica Neue", sans-serif',
  // Global component defaults. Set here (not per-instance) so every Mantine
  // Button across the app shares one rounded style and can be restyled in a
  // single place later as more screens are migrated off MUI.
  components: {
    Button: {
      defaultProps: {
        radius: 'xl',
      },
    },
    // Mantine renders Select dropdowns, Tooltips and Popovers in a portal at a
    // default z-index of 300. Several dialogs (e.g. AdminTaskDialog) raise their
    // Modal z-index well above that (1500-2100) so they sit over the legacy MUI
    // chrome — which would bury those dropdowns/tooltips behind the modal.
    // Lift them above every modal so Selects open and Tooltips show.
    Select: {
      defaultProps: {
        comboboxProps: { zIndex: 3000 },
      },
    },
    Tooltip: {
      defaultProps: {
        zIndex: 3000,
      },
    },
    // Same reason: the locale colour pickers live in a Popover inside the
    // locale edit/create Modals (z-index 2000), so at the default 300 the
    // dropdown opened behind the modal and the picker looked dead.
    Popover: {
      defaultProps: {
        zIndex: 3000,
      },
    },
  },
  // Arbitrary custom tokens that don't fit Mantine's 10-shade color model.
  other: {
    brandPrimary: BRAND_PRIMARY,
    brandSecondary: BRAND_SECONDARY,
    halfToneColour: HALF_TONE,
    mutedColour: MUTED,
  },
});
